import React, { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import { FormItem, FormLabel, FormControl } from "src/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { createTask, updateTask } from "../api/tasksApi"

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
]

function toLocalDatetimeValue(date) {
  if (!date) return ""
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function TaskFormDrawer({ open, onClose, onSaved, task = null, parent = null, members = [] }) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const isEdit = !!task
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    due_at: "",
    assignee_user_id: "",
  })

  useEffect(() => {
    if (!open) return
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || "medium",
        due_at: toLocalDatetimeValue(task.due_at),
        assignee_user_id: task.assignee?.id ? String(task.assignee.id) : "",
      })
    } else {
      setForm({ title: "", description: "", priority: "medium", due_at: "", assignee_user_id: "" })
    }
  }, [open, task])

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.title.trim()) return
    setSaving(true)
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        due_at: form.due_at || null,
        assignee_user_id: form.assignee_user_id || null,
      }
      const saved = isEdit
        ? await updateTask(task.id, payload)
        : await createTask(payload, parent)
      toast({ title: isEdit ? "Task updated" : "Task created" })
      onSaved?.(saved)
      onClose()
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save task", description: err.response?.data?.errors?.[0] })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit task" : "New task"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update the task details below." : "Add a task to track an action item."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Title */}
            <FormItem>
              <FormLabel>
                Title <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  placeholder="e.g. Call back the lead"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                />
              </FormControl>
            </FormItem>

            {/* Description */}
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  rows={3}
                  placeholder="Optional details…"
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                />
              </FormControl>
            </FormItem>

            {/* Priority */}
            <FormItem>
              <FormLabel>Priority</FormLabel>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>

            {/* Due date */}
            <FormItem>
              <FormLabel>Due date</FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  value={form.due_at}
                  onChange={(e) => set("due_at", e.target.value)}
                />
              </FormControl>
            </FormItem>

            {/* Assignee */}
            {members.length > 0 && (
              <FormItem>
                <FormLabel>Assign to</FormLabel>
                <Select
                  value={form.assignee_user_id || "unassigned"}
                  onValueChange={(v) => set("assignee_user_id", v === "unassigned" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !form.title.trim()} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Create task"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
